type WithStaleness<TData> =
  | {
      stale: true;
      data: TData;
    }
  | {
      stale: false;
      data?: undefined;
    };

type LoadingState<TData> = WithStaleness<TData> & {
  error: null;
  loading: true;
};

type ErrorState<TData, TError extends Error = Error> = WithStaleness<TData> & {
  error: TError;
  loading: false;
};

type EmptyState<__TData> = {
  data?: undefined;
  error: null;
  loading: false;
  stale: false;
};

// Stale or not, Success will *always* have data
type SuccessState<TData, TStale extends boolean = boolean> = {
  data: TData;
  error: null;
  loading: false;
  stale: TStale;
};

export type ResolverState<TData, TError extends Error = Error> =
  | LoadingState<TData>
  | SuccessState<TData>
  | ErrorState<TData, TError>
  | EmptyState<TData>;

/**
 * Produce an empty state. Used for clearing state and the hook's "skipped" scenario.
 *
 * Discards all values from the current state.
 *
 * @example
 * ```ts
 *
 * toEmpty()() = {
 *   data: undefined,
 *   error: null,
 *   loading: false,
 *   stale: false
 * }
 */
export const toEmpty =
  <TData>(__state?: ResolverState<TData, any>) =>
  (): EmptyState<TData> => ({
    data: undefined,
    error: null,
    loading: false,
    stale: false,
  });

/**
 * Produce a loading state. Forwards the current `data` if present and sets the stale flag accordingly.
 *
 * @example
 * ```ts
 *
 * const data = {
 *   data: 'foo',
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * toLoading()(data) = {
 *   data: 'foo', // Copies data
 *   error: null,
 *   loading: true, // Sets loading
 *   stale: true // Sets stale: true
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toLoading())
 * ```
 */
export const toLoading =
  () =>
  <TData>(state?: ResolverState<TData, any>): LoadingState<TData> =>
    state?.data !== undefined
      ? {
          data: state.data,
          error: null,
          loading: true,
          stale: true,
        }
      : {
          data: undefined,
          error: null,
          loading: true,
          stale: false,
        };

/**
 * Produce a success state with the given data.
 *
 * @example
 * ```ts
 *
 * const data = { id: '1' };
 *
 * toSuccess(data)() = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toSuccess(data))
 * ```
 */
export const toSuccess =
  <TData>(nextData: TData) =>
  (): SuccessState<TData, false> => ({
    data: nextData,
    error: null,
    loading: false,
    stale: false,
  });

/**
 * Produce an error state. Forwards the current `data` if present and sets the stale flag accordingly.
 *
 * @example
 * ```ts
 *
 * const state = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * toError(new Error('Failed to fetch'))(state) = {
 *   data: { id: '1' },
 *   error: Error('Failed to fetch'),
 *   loading: false,
 *   stale: true
 * }
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(toError(new Error('Failed to fetch')))
 * ```
 */
export const toError =
  <TError extends Error = Error>(error: TError) =>
  <TData>(state?: ResolverState<TData, any>): ErrorState<TData, TError> =>
    state?.data === undefined
      ? {
          data: undefined,
          error,
          loading: false,
          stale: false,
        }
      : {
          data: state.data,
          error,
          loading: false,
          stale: true,
        };

/**
 * Mark a state as stale if data is present.
 *
 * @example
 * ```ts
 *
 * const state = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: false
 * };
 *
 * markStale()(state) = {
 *   data: { id: '1' },
 *   error: null,
 *   loading: false,
 *   stale: true
 * }
 *
 * const noDataState = {
 *   error: null,
 *   loading: true,
 *   stale: false
 * };
 *
 * markStale()(noDataState) = {
 *   error: null,
 *   loading: true,
 *   stale: false
 * };
 *
 * // Curried for use in conjunction with `withFunctionalNext` to automatically derive the correct state:
 *
 * runtime.next(markStale())
 * ```
 */
export const markStale =
  () =>
  <TData, TError extends Error = Error>(
    state: ResolverState<TData, TError>
  ): ResolverState<TData, TError> =>
    state.data === undefined
      ? { ...state }
      : {
          ...state,
          data: state.data,
          stale: true,
        };

/**
 * Merge source state flags (loading, error, stale) with target data,
 * ensuring the result satisfies ResolverState type constraints.
 *
 * This helper safely combines state flags from one resource with data from another,
 *
 * Primarily used in link transforms when inheriting parent state with derived child data.
 */
export const applyState = <TStateSourceData, TTargetData>(
  sourceState: ResolverState<TStateSourceData, any>,
  targetState?: ResolverState<TTargetData, any>
): ResolverState<TTargetData> | void => {
  // If source is loading, we're loading.
  if (sourceState.loading) {
    return toLoading()(targetState);
  }

  // If source is in error state, copy the error over
  if (sourceState.error) {
    return toError(sourceState.error)(targetState);
  }

  if (targetState?.data !== undefined) {
    const successState = toSuccess(targetState.data)();

    if (sourceState.stale) {
      return markStale()(successState);
    }

    return successState;
  }
};
